import React, { useState, ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'
import Navigation from './Navigation'
import { useSEO } from '../hooks/useSEO'
import './Layout.css'

interface LayoutProps {
  children: ReactNode
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const location = useLocation()
  
  // Automatycznie zarządzaj SEO meta tagami (w tym canonical URL)
  useSEO()

  // Strony, które mają wyświetlać bottomBar
  const pagesWithBottomBar = ['/szkolenia', '/biblioteka', '/forum']
  const showBottomBar = pagesWithBottomBar.includes(location.pathname)

  // Strony, na których ukrywamy footer (logowanie i rejestracja)
  const hideFooterPages = ['/logowanie', '/rejestracja']
  const shouldShowFooter = !hideFooterPages.includes(location.pathname)

  return (
    <div className="layout">
      {/* Skip Links - WCAG 2.1 AAA Keyboard Navigation */}
      <a href="#main-content" className="skip-link">
        Przejdź do treści głównej
      </a>
      <a href="#navigation" className="skip-link">
        Przejdź do nawigacji
      </a>

      {/* Header - Radix UI Premium */}
      <header className="header" role="banner">
        <div className="container">
          <Link to="/" className="logo">
            <strong>wojskaspecjalne.pl</strong>
          </Link>
          <Navigation isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} />
        </div>
      </header>

      <main id="main-content" className="main-content" role="main">
        {children}
      </main>

      {shouldShowFooter && (
        <footer className="footer" role="contentinfo">
        <div className="container">
          <div className="footer-content">
            {/* Sekcja o platformie */}
            <section className="footer-section" aria-labelledby="footer-about">
              <h3 id="footer-about" className="footer-title">wojskaspecjalne.pl</h3>
              <p className="footer-description">
                Platforma edukacyjna rozwijająca samodzielne, zadaniowe, krytyczne myślenie 
                według dorobku Józefa Kosseckiego i Mariana Mazura.
              </p>
              <ul className="footer-social" aria-label="Wsparcie">
                <li>
                  <Link to="/wsparcie" aria-label="Wspieraj - Przekaż darowiznę" className="social-link social-link-bitcoin">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M23.638 14.904c-1.602 6.43-8.113 10.34-14.542 8.736C2.67 22.05-1.244 15.525.362 9.105 1.962 2.67 8.475-1.243 14.9.358c6.43 1.605 10.342 8.115 8.738 14.548v-.002zm-6.35-4.613c.24-1.59-.974-2.45-2.64-3.03l.54-2.153-1.315-.33-.525 2.107c-.345-.087-.705-.167-1.064-.25l.526-2.127-1.32-.33-.54 2.165c-.285-.067-.565-.132-.84-.2l-1.815-.45-.35 1.407s.975.225.955.236c.535.136.63.486.615.766l-1.477 5.92c-.075.166-.24.406-.614.314.015.02-.96-.24-.96-.24l-.66 1.51 1.71.426.93.242-.54 2.19 1.32.327.54-2.17c.36.1.705.19 1.05.273l-.51 2.154 1.32.33.545-2.19c2.24.427 3.93.257 4.64-1.774.57-1.637-.03-2.58-1.217-3.196.854-.193 1.5-.76 1.68-1.93h.01zm-3.01 4.22c-.404 1.64-3.157.75-4.05.53l.72-2.9c.896.23 3.757.67 3.33 2.37zm.41-4.24c-.37 1.49-2.662.735-3.405.55l.654-2.64c.744.18 3.137.524 2.75 2.084v.006z"/>
                    </svg>
                  </Link>
                </li>
              </ul>
            </section>

            {/* Sekcja - Linki nawigacyjne */}
            <nav className="footer-section" aria-labelledby="footer-nav">
              <h4 id="footer-nav" className="footer-heading">Nawigacja</h4>
              <ul className="footer-links" role="list">
                <li><Link to="/">Strona Główna</Link></li>
                <li><Link to="/biblioteka">Biblioteka</Link></li>
                <li><Link to="/biblioteka/autorzy">Autorzy</Link></li>
                <li><Link to="/forum">Forum</Link></li>
                <li><Link to="/mapa-strony">Mapa Strony</Link></li>
                <li>
                  <a 
                    href="https://naiwarszawa.pl/sklep/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    Księgarnia NAI
                  </a>
                </li>
              </ul>
            </nav>

            {/* Sekcja - Informacje */}
            <nav className="footer-section" aria-labelledby="footer-info">
              <h4 id="footer-info" className="footer-heading">Informacje</h4>
              <ul className="footer-links" role="list">
                <li><Link to="/wsparcie">Jak Nas Wesprzeć</Link></li>
                <li><Link to="/o-nas">O Nas</Link></li>
                <li><Link to="/kontakt">Kontakt</Link></li>
                <li><Link to="/faq">FAQ</Link></li>
                <li><Link to="/polityka-prywatnosci">Polityka Prywatności</Link></li>
                <li><Link to="/regulamin">Regulamin</Link></li>
              </ul>
            </nav>
          </div>

          {/* Stopka dolna - Copyright */}
          <div className="footer-bottom">
            <p className="footer-copyright">
              &copy; {new Date().getFullYear()} wojskaspecjalne.pl - Projekt non-profit realizowany przez{' '}
              <a 
                href="https://epko.tech" 
                target="_blank" 
                rel="noopener noreferrer"
                className="footer-link"
              >
                EPKO Tech
              </a>
            </p>
            <p className="footer-tagline">
              Rozwijamy samodzielne, zadaniowe, krytyczne myślenie
            </p>
          </div>
        </div>
      </footer>
      )}
    </div>
  )
}

export default Layout

